import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import { getCountryFromIP, getURLSearchParams, goToTopOfPage } from '../../helper/helper';
import Footer from '../../components/Common/Footer';
import { init, trackPageView } from 'goaffpro-client';
import NavigationBar from '../../components/NavigationBar/NavigationBar';
import { BookModel } from '../../interfaces/book.interface';
import { setDraftId } from '../../store/appSlice';
import { AppDispatch, RootState } from '../../store/store';
import { useDispatch, useSelector } from 'react-redux';
import {
  CountryCurrencyEnum,
  CountryCurrencySymbolEnum, CurrencySymbol,
  getCountries,
  postalCodeFormatMap,
} from '../../config/countries';
import { getUSStates } from '../../config/usa-states';
import { enhancedValidationSchema, validationStudentBookSchema } from './services/BookPurchaseValidationSchemes';
import { BillingFormErrors } from './models/BillingFormErrors';
import { calculateBookCosts } from './services/BookCostCalculate';
import { getBookDetailsService, handleApplyPromoCode, handlePurchaseSubmit } from './services/BookPurchase.service';
import PrintedBookSummary from './components/PrintedBookSummary';
import ShippingInformationForm from './components/ShippingInformationForm';
import ThankYouMessage from './components/ThankYouMessage';
import PurchaseHeader from './components/PurchaseHeader';
import { UserLocalStorageKeysEnum, UserShippingAddress } from '../../interfaces/user.interface';
import { CONFIG } from '../../config/mainConfig';
import { runEditStory } from '../../store/storySlice';
import { Toast } from '../../components/Common/Toast';
import { CurrencyEnum } from '../../interfaces/plan.interface';
import { getCurrencyFromCountry, getCurrencySymbolFromCountry } from '../../utils/currencyHelper';
import { useStudentVPCStatus } from '../../hooks/useStudentVPCStatus';

init(process.env.REACT_APP_GOAFFPRO_PUBLIC_KEY!);

interface BookPurchaseProps {
  isShare?: boolean;
}

const BookPurchase = ({ isShare }: BookPurchaseProps) => {
  const navigate = useNavigate();
  let { bookId } = useParams();
  const inlineQueryParams = getURLSearchParams(true);
  const user = useSelector((state: RootState) => state.user.user);
  const { vpcRequired, vpcComplete, teacherState, isVPCBlocked } = useStudentVPCStatus();
  const singleBookPlan = useSelector((state: RootState) => state.user.singleBookPlan);
  const stripe = useStripe();
  const elements = useElements();
  const [usaStates, setUsaStates] = useState<Array<string>>([]);
  const [countries, setCountries] = useState<Array<string>>([]);
  const [bookDetails, setBookDetails] = useState<BookModel | null>(null);
  const [isBillingSameAsShipping, setIsBillingSameAsShipping] = useState<boolean>(true);
  const [paymentLoader, setPaymentLoader] = useState<boolean>(false);
  const [couponLoader, setCouponLoader] = useState<boolean>(false);
  const [errors, setErrors] = useState<BillingFormErrors>({});
  const [purchaseSuccess, setPurchaseSuccess] = useState<boolean>(false);
  const [bookCost, setBookCost] = useState<number | null>(12.95);
  const [bookQty, setBookQty] = useState(1);
  const [shippingCost, setShippingCost] = useState<number>(6.99);
  const [discount, setDiscount] = useState<number | null>(null);
  const [additionalPageCost, setAdditionalPageCost] = useState<number>(0);
  const [bookTotalCost, setBookTotalCost] = useState<number | null>(null);
  const [couponClass, setCouponClass] = useState<string | null>(null);
  const [netCost, setNetCost] = useState<number | null>(null);
  const [couponDescription, setCouponDescription] = useState<string | null>(null);
  const [couponType, setCouponType] = useState<string | null>(null);
  const [totalPages, setTotalPages] = useState<number | null>(null);
  const [readonlyShippingAddress, setReadonlyShippingAddress] = useState<boolean>(false);
  const [defaultAuthorShippingAddress, setDefaultAuthorShippingAddress] = useState<UserShippingAddress | null>(null);
  const dispatch: AppDispatch = useDispatch();

  const [values, setValues] = useState({
    shippingEmail: '',
    recipientName: '',
    customerName: '',
    shippingAddress1: '',
    shippingAddress2: '',
    shippingState: '',
    shippingCountry: 'United States',
    shippingCity: '',
    shippingZip: '',
    billingCardHolderName: '',
    billingAddress1: '',
    billingAddress2: '',
    billingCity: '',
    billingPostalCode: '',
    coupon: '',
    qty: 1,
    billingState: '',
    cardHolderName: '',
  });
  const [autocompletedCountryRequest, setAutocompletedCountryRequest] = useState<boolean>(false);
  const [autocompletedCountry, setAutocompletedCountry] = useState<string | null>(null);
  const [currencySymbol, setCurrencySymbol] = useState<CurrencySymbol | null>(null);
  const [selectedCurrency, setSelectedCurrency] = useState<CurrencyEnum>(CurrencyEnum.USD);

  useEffect(() => {
    if (autocompletedCountryRequest) {
      return;
    }
    setAutocompletedCountryRequest(true);
    getCountryFromIP().then((countryCode) => {
      if (countryCode) {
        const listCountries = Object.values(postalCodeFormatMap);
        const country = listCountries.find((c) => c.code.toLowerCase() === countryCode.toLowerCase());
        if (country) {
          setAutocompletedCountry(country.country);
        }
      }
    });
  }, [values, autocompletedCountryRequest]);

  useEffect(() => {
    if (values.shippingCountry) {
      // Get currency from country selection
      const currency = getCurrencyFromCountry(values.shippingCountry);
      setSelectedCurrency(currency);
      
      // Get currency symbol for display
      const symbol = getCurrencySymbolFromCountry(values.shippingCountry);
      setCurrencySymbol(symbol as CurrencySymbol);
    }
  }, [values.shippingCountry]);

  useEffect(() => {
    setUsaStates(getUSStates());
    setCountries(getCountries());
    trackPageView();
  }, []);

  useEffect(() => {
    setValues((prevState) => ({
      ...prevState,
      shippingEmail: user?.email || '',
      customerName: user?.customerName || '',
      shippingAddress1: user?.shippingAddress1 || '',
      shippingAddress2: user?.shippingAddress2 || '',
      shippingState: user?.shippingState || '',
      shippingCountry: user?.shippingCountry || prevState.shippingCountry || 'United States',
      shippingCity: user?.shippingCity || '',
      shippingZip: user?.shippingZip || '',
      billingCardHolderName: user?.billingCardHolderName || '',
      billingAddress1: user?.billingAddress1 || '',
      billingAddress2: user?.billingAddress2 || '',
      billingCity: user?.billingCity || '',
      billingPostalCode: user?.billingPostalCode || '',
      coupon: discount ? user?.coupon! : '',
      billingState: user?.billingState || '',
    }));
  }, [user]);

  const getBookDetails = async () => {
    try {
      const { book, additionalPageCost, totalPages, readonlyShipping, shippingAddress } = await getBookDetailsService(
        bookId!,
        isShare,
      );
      if (book) {
        setBookDetails(book);
        setTotalPages(totalPages);
        setAdditionalPageCost(additionalPageCost);
        setReadonlyShippingAddress(!!readonlyShipping);
        setShippingAddress(shippingAddress);
      }
    } catch (error) {}
  };

  const setShippingAddress = (shippingAddress: UserShippingAddress | null | undefined) => {
    if (!shippingAddress) return;
    setDefaultAuthorShippingAddress(shippingAddress);
    const sessionEmailAddress = sessionStorage.getItem('userEmailAddress');
    setValues((prevValues) => ({
      ...prevValues,
      shippingEmail: sessionEmailAddress || prevValues?.shippingEmail,
      customerName: shippingAddress.shippingName || '',
      recipientName: shippingAddress.recipientName || '',
      shippingAddress1: shippingAddress.shippingAddress1 || '',
      shippingAddress2: shippingAddress.shippingAddress2 || '',
      shippingState: shippingAddress.shippingState || '',
      shippingCountry: shippingAddress.shippingCountry || prevValues.shippingCountry || 'United States',
      shippingCity: shippingAddress.shippingCity || '',
      shippingZip: shippingAddress.shippingZip || '',
    }));
  };

  useEffect(() => {
    if (bookId) getBookDetails();
  }, [bookId]);

  useEffect(() => {
    localStorage.removeItem(UserLocalStorageKeysEnum.BOOK_ID);
    localStorage.removeItem(UserLocalStorageKeysEnum.EDIT_MODE);
    localStorage.removeItem(UserLocalStorageKeysEnum.STORY_STEP);
    dispatch(setDraftId(null));
    goToTopOfPage();
  }, []);

  const handleChange = (e: { target: { name: string; value: any } }) => {
    const { name, value } = e.target;
    setValues((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));
    if (name === 'qty') {
      setBookQty(value);
    }

    copyShippingToBilling();
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    await handlePurchaseSubmit({
      values,
      validationSchema: bookDetails?.isStudentBook ? validationStudentBookSchema : enhancedValidationSchema,
      stripe,
      elements,
      user,
      bookId,
      bookQty,
      additionalPageCost,
      bookTotalCost,
      shippingCost,
      setPaymentLoader,
      setPurchaseSuccess,
      setErrors,
      isShare,
      bookDetails,
      selectedCurrency,
    });
  };
  useEffect(() => {
    let newbookCost: any = 0;
    let newNetCost: any = 0;

    newbookCost = (bookCost || 12.95) + (additionalPageCost || 0);
    let discountAmountPerBook = discount ? +discount : 0;
    //if user is on plan with no free physical book

    const freeBooksAllowed = (user?.freePhysicalBookQuantity || 0) - (user?.freePhysicalBookCreated || 0);
    if (
      user?.plan?.freePhysicalBookStatus !== true ||
      ((user?.freePhysicalBookCreated || 0) >= (user?.freePhysicalBookQuantity || 0) && freeBooksAllowed === 0)
    ) {
      if (!discount) {
        newNetCost = (
          parseFloat(String(newbookCost)) * parseFloat(String(bookQty)) +
          parseFloat(String(shippingCost))
        ).toFixed(2);
      } else {
        newNetCost = (
          parseFloat(String(newbookCost)) * parseFloat(String(bookQty)) +
          CONFIG.BILLING.SHIPPING_COST -
          parseFloat(String(discountAmountPerBook))
        ).toFixed(2);
        if (newNetCost < 0) {
          newNetCost = 0;
        } else {
          newNetCost = parseFloat(newNetCost).toFixed(2);
        }
      }

      setBookTotalCost(Number((parseFloat(newbookCost) * parseFloat(String(bookQty))).toFixed(2)));
      setNetCost(newNetCost);
    } else {
      //if user is on plan with free physical book and can purchase free physical book

      if ((user?.freePhysicalBookCreated || 0) < (user?.freePhysicalBookQuantity || 0) && freeBooksAllowed > 0) {
        //if book quantity for purchase, is less then the free books available as per plan

        if (bookQty <= freeBooksAllowed) {
          setBookTotalCost(0.0);
          setBookCost(0.0);
          setNetCost(0.0);
          setShippingCost(0.0);
        } else {
          //if book quantity is greater then the free books available

          newbookCost = 12.95 + parseFloat(String(additionalPageCost));

          const totalCost = calculateBookCosts(
            freeBooksAllowed,
            newbookCost,
            discountAmountPerBook,
            additionalPageCost,
            bookQty,
            couponType,
            discount,
          );

          setBookTotalCost(totalCost.setBookTotalCost);
          setNetCost(totalCost.setNetCost !== undefined ? Number(totalCost.setNetCost) : null);
          setBookCost(totalCost.setBookCost !== undefined ? Number(totalCost.setBookCost) : null);
          setShippingCost(totalCost.setShippingCost !== undefined ? Number(totalCost.setShippingCost) : 0);
        }
      }
    }
  }, [bookCost, additionalPageCost, bookQty, discount]);

  const copyShippingToBilling = () => {
    if (isBillingSameAsShipping) {
      setValues((prevValues) => ({
        ...prevValues,
        billingCardHolderName: prevValues.customerName,
        billingAddress1: prevValues.shippingAddress1,
        billingAddress2: prevValues.shippingAddress2,
        billingCity: prevValues.shippingCity,
        billingPostalCode: prevValues.shippingZip,
        billingState: prevValues.shippingState,
      }));
    }
  };

  const isPreviewMode = () => {
    return inlineQueryParams.get('mode') === 'preview';
  };

  const goBack = () => {
    if (isPreviewMode()) {
      !!user?.stripeSubscriptionId || !!user?.isTrialFreeAccess
        ? dispatch(runEditStory({ book: bookDetails, navigate: navigate }))
        : Toast('You need an active membership to edit your story', true);
    } else {
      navigate(-1);
    }
  };
  useEffect(() => {
    if (isBillingSameAsShipping) copyShippingToBilling();
  }, [isBillingSameAsShipping]);

  const applyPromoCode = async (couponCode: string) => {
    await handleApplyPromoCode({
      couponCode,
      bookTotalCost,
      userEmail: user?.email,
      setCouponClass,
      setDiscount,
      setCouponDescription,
      setCouponType,
      setCouponLoader,
    });
  };

  // Show VPC message if required for students
  if (isVPCBlocked()) {
    return (
      <div className="App mx-auto bg-storytan">
        <NavigationBar />
        <div className="min-h-screen bg-gray-50 flex flex-col justify-center items-center p-4">
          <div className="max-w-md text-center">
            <div className="mb-6">
              <svg className="mx-auto h-16 w-16 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              Book Purchase Not Available
            </h1>
            
            <p className="text-gray-600 mb-4">
              Your teacher is located in <strong>{teacherState || 'Illinois'}</strong>, which requires Verified Parental Consent for book purchases.
            </p>
            
            <p className="text-sm text-gray-500 mb-6">
              Please ask your teacher to complete the parental consent form before you can purchase this book.
            </p>
            
            <button
              onClick={() => navigate('/')}
              className="bg-storyblue text-white px-6 py-2 rounded-lg hover:bg-storyblueSecondLightText transition-colors"
            >
              Return Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="App mx-auto bg-storytan">
      <NavigationBar />
      <div className={`max-w-7xl bg-storytan p-8 mx-auto`}>
        {purchaseSuccess ? (
          <ThankYouMessage isClassShipment={!!bookDetails?.isStudentBook} />
        ) : (
          <div>
            <PurchaseHeader goBack={goBack} />

            {/* Direct orders print immediately — never show a payable form for a book the print
                pipeline would refuse. The server rejects such orders too; this is the friendly face. */}
            {bookDetails && !bookDetails.isStudentBook && bookDetails.isPrintReady === false ? (
              <div className="flex justify-center md:mt-8">
                <div className="max-w-lg w-full rounded-xl border border-amber-200 bg-amber-50 text-amber-900 px-6 py-8 text-center mt-8 md:mt-0">
                  <div className="text-3xl mb-3">🎨</div>
                  <h2 className="text-lg font-bold mb-2">This book is still being finished</h2>
                  <p className="text-sm mb-4">
                    Ordering opens once every page is ready to print
                    {bookDetails.printReadyReasons?.length ? ` (${bookDetails.printReadyReasons[0].toLowerCase()})` : ''}.
                    Please check back soon — nothing is charged until the book is complete.
                  </p>
                  <button
                    onClick={goBack}
                    className="bg-storyblue text-white px-6 py-2 rounded-lg hover:bg-storyblueSecondLightText transition-colors text-sm font-bold"
                  >
                    Back to the book
                  </button>
                </div>
              </div>
            ) : (
            <div className="flex flex-col sm:flex-row md:mt-8">
              <div className="flex flex-col gap-8 mt-8 md:mt-0 text-left w-full mr-8">
                {/* Shipping-mode banner: buyers must know BEFORE paying whether this order joins
                    the one-time Class Shipment or ships straight to them. isStudentBook mirrors
                    the server's pooling rule (teacher window / student-authored). */}
                {bookDetails && (
                  <div
                    className={`rounded-xl border px-4 py-3 text-sm ${
                      bookDetails?.isStudentBook
                        ? 'bg-blue-50 border-blue-200 text-blue-900'
                        : 'bg-amber-50 border-amber-200 text-amber-900'
                    }`}
                  >
                    {bookDetails?.isStudentBook ? (
                      <>
                        <span className="font-bold">📦 Part of the Class Shipment.</span> All class orders are printed
                        together and delivered to the school in one box — the teacher hands the books out. No shipping
                        address needed. After the Class Shipment is sent, any extra copies you order ship straight to
                        your home.
                        {bookDetails.isPrintReady === false && (
                          <>
                            {' '}
                            <span className="font-bold">Note:</span> this book is still being finished — it will join
                            the Class Shipment once it's ready.
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        <span className="font-bold">🚚 Ships directly to you.</span> This order isn't part of a Class
                        Shipment — it prints right away and ships to the address you enter.
                      </>
                    )}
                  </div>
                )}
                <PrintedBookSummary
                  bookQty={bookQty}
                  bookCost={bookCost!}
                  additionalPageCost={additionalPageCost}
                  totalPages={totalPages!}
                  bookTotalCost={bookTotalCost!}
                  shippingCost={shippingCost}
                  discount={discount}
                  netCost={netCost!}
                  currencySymbol={currencySymbol}
                  bookDetails={bookDetails}
                />
              </div>

              <ShippingInformationForm
                isShare={isShare}
                readonlyShippingAddress={readonlyShippingAddress}
                bookDetails={bookDetails}
                user={user}
                autocompletedCountry={autocompletedCountry}
                values={values}
                errors={errors}
                handleChange={handleChange}
                handleSubmit={handleSubmit}
                applyPromoCode={applyPromoCode}
                couponClass={couponClass!}
                couponLoader={couponLoader}
                setValues={setValues}
                bookCost={bookCost!}
                netCost={netCost!}
                shippingCost={shippingCost}
                bookTotalCost={bookTotalCost!}
                discount={discount}
                couponDescription={couponDescription}
                isBillingSameAsShipping={isBillingSameAsShipping}
                setIsBillingSameAsShipping={setIsBillingSameAsShipping}
                singleBookPlan={singleBookPlan}
                usaStates={usaStates}
                countries={countries}
                paymentLoader={paymentLoader}
                goBack={goBack}
              />
            </div>
            )}
          </div>
        )}
      </div>
      <Footer className="max-w-100 mb-0 pb-5 pt-4" />
    </div>
  );
};

export default BookPurchase;
